import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ShoppingCart, ArrowLeft, Star, ShieldCheck, RefreshCw, Truck, Heart } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config';

// Remove hardcoded API since we use relative proxy in AuthContext or direct axios calls

const ProductScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { user, toggleFavorite, isFavorite, getConfig } = useAuth();
  const [product, setProduct] = useState({});
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);

  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);

  const fetchProduct = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/products/${id}`);
      setProduct(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching product:', error);
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  const handleAddToCart = () => {
    addToCart(product, qty);
    navigate('/cart');
  };

  const submitReviewHandler = async (e) => {
    e.preventDefault();
    if (!comment) return alert('Please enter a comment');
    setReviewLoading(true);
    try {
      await axios.post(
        `${API_URL}/products/${id}/reviews`,
        { rating, comment },
        getConfig()
      );
      alert('Review submitted successfully!');
      setComment('');
      setRating(5);
      fetchProduct();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to submit review');
    } finally {
      setReviewLoading(false);
    }
  };

  if (loading) return <div className="container mx-auto px-4 py-20 text-center text-primary-600 font-bold">Loading product details...</div>;

  const fav = isFavorite(product._id);

  return (
    <div className="container mx-auto px-4 py-12">
      <Link to="/" className="inline-flex items-center space-x-2 text-secondary-600 hover:text-primary-600 transition-colors mb-8 font-medium">
        <ArrowLeft className="h-5 w-5" />
        <span>Back to Products</span>
      </Link>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
        <div className="relative group">
          <div className="absolute -inset-4 bg-primary-100/50 rounded-[40px] blur-2xl transform group-hover:scale-105 transition-transform duration-500" />
          <img src={product.image} alt={product.name} className="relative w-full rounded-[32px] shadow-2xl object-cover aspect-square" />
        </div>

        <div className="space-y-8">
          <div className="space-y-4">
            <div className="flex items-center space-x-4">
              <span className="bg-primary-100 text-primary-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest">
                {product.category?.name || product.category || 'General'}
              </span>
              <div className="flex items-center space-x-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className={`h-4 w-4 ${star <= (product.rating || 4) ? 'text-yellow-400 fill-current' : 'text-secondary-200'}`} />
                ))}
                <span className="text-sm font-medium text-secondary-500 ml-2">({product.numReviews || 0} reviews)</span>
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-secondary-900 leading-tight">{product.name}</h1>
            <p className="text-secondary-600 text-lg leading-relaxed">{product.description}</p>
          </div>

          <div className="space-y-2">
            <div className="flex items-baseline space-x-4">
              <span className="text-4xl font-black text-secondary-900">₹{product.price}</span>
              {product.mrp > product.price && (
                <span className="text-secondary-400 line-through text-xl font-medium">₹{product.mrp}</span>
              )}
              {(product.offer || product.discount > 0 || product.mrp > product.price) && (
                <span className="bg-red-600 text-white px-3 py-1 rounded-xl text-xs font-black uppercase tracking-wider shadow-md">
                  {product.offer || `${product.discount || Math.round(((product.mrp - product.price) / product.mrp) * 100)}% OFF`}
                </span>
              )}
            </div>
            {product.mrp > product.price && (
              <p className="text-xs font-bold text-emerald-600">
                You save ₹{product.mrp - product.price} ({Math.round(((product.mrp - product.price) / product.mrp) * 100)}% discount)
              </p>
            )}
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-secondary-50 rounded-2xl border border-secondary-100">
              <span className="font-semibold text-secondary-900">Availability</span>
              <span className={`font-bold ${product.countInStock > 0 ? 'text-primary-600' : 'text-red-500'}`}>
                {product.countInStock > 0 ? `In Stock (${product.countInStock})` : 'Out of Stock'}
              </span>
            </div>

            {product.countInStock > 0 && (
              <div className="flex items-center space-x-4">
                <div className="flex items-center border border-secondary-200 rounded-2xl p-1 bg-white">
                  <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 hover:bg-secondary-50 rounded-xl transition-colors font-bold text-lg w-12">-</button>
                  <span className="w-12 text-center font-bold text-lg">{qty}</span>
                  <button onClick={() => setQty(Math.min(product.countInStock, qty + 1))} className="p-3 hover:bg-secondary-50 rounded-xl transition-colors font-bold text-lg w-12">+</button>
                </div>
                <button onClick={handleAddToCart} className="flex-1 bg-primary-600 hover:bg-primary-700 text-white px-8 py-4 rounded-2xl font-bold transition-all shadow-xl shadow-primary-200 flex items-center justify-center space-x-3">
                  <ShoppingCart className="h-6 w-6" />
                  <span>Add to Cart</span>
                </button>
                <button 
                  onClick={() => {
                    if (!user) {
                      alert('Please log in to add products to your favorites.');
                      return;
                    }
                    toggleFavorite(product._id);
                  }} 
                  className={`p-4 rounded-2xl border transition-all ${fav ? 'bg-red-50 border-red-200 text-red-500' : 'bg-white border-secondary-200 text-secondary-400 hover:text-red-500'}`}
                >
                  <Heart className={`h-6 w-6 ${fav ? 'fill-current text-red-500' : ''}`} />
                </button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-8 border-t border-secondary-100">
            <div className="flex items-center space-x-3 text-secondary-600">
              <Truck className="h-5 w-5 text-primary-500" />
              <span className="text-sm font-medium">Free Delivery</span>
            </div>
            <div className="flex items-center space-x-3 text-secondary-600">
              <RefreshCw className="h-5 w-5 text-primary-500" />
              <span className="text-sm font-medium">Easy Returns</span>
            </div>
            <div className="flex items-center space-x-3 text-secondary-600">
              <ShieldCheck className="h-5 w-5 text-primary-500" />
              <span className="text-sm font-medium">Quality Check</span>
            </div>
          </div>
        </div>
      </div>

      {/* Reviews Section */}
      <div className="mt-20 border-t border-secondary-100 pt-12 max-w-4xl">
        <h2 className="text-2xl font-black italic text-secondary-900 uppercase tracking-tight mb-8">Customer Reviews</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {/* Review form */}
          <div className="md:col-span-1 space-y-6">
            <h3 className="font-bold text-secondary-900 text-lg">Write a Review</h3>
            {user ? (
              <form onSubmit={submitReviewHandler} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-xs font-black text-secondary-400 uppercase tracking-widest">Rating</label>
                  <select
                    value={rating}
                    onChange={(e) => setRating(Number(e.target.value))}
                    className="w-full bg-secondary-50 border border-secondary-100 rounded-xl py-3 px-4 focus:ring-2 focus:ring-primary-500 outline-none font-bold text-secondary-900"
                  >
                    <option value="5">5 - Excellent</option>
                    <option value="4">4 - Very Good</option>
                    <option value="3">3 - Good</option>
                    <option value="2">2 - Fair</option>
                    <option value="1">1 - Poor</option>
                  </select>
                </div>
                
                <div className="space-y-2">
                  <label className="text-xs font-black text-secondary-400 uppercase tracking-widest">Comment</label>
                  <textarea
                    rows="4"
                    required
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    className="w-full bg-secondary-50 border border-secondary-100 rounded-2xl py-3 px-4 focus:ring-2 focus:ring-primary-500 outline-none text-secondary-900 font-medium"
                    placeholder="Share your thoughts about this product..."
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={reviewLoading}
                  className="w-full bg-primary-600 hover:bg-primary-700 disabled:bg-primary-300 text-white py-3 rounded-xl font-bold transition-all shadow-lg shadow-primary-200"
                >
                  {reviewLoading ? 'Submitting...' : 'Submit Review'}
                </button>
              </form>
            ) : (
              <div className="p-6 bg-secondary-50 rounded-2xl text-center space-y-2">
                <p className="text-secondary-600 font-medium text-sm">You must be logged in to write a review.</p>
                <Link to={`/login?redirect=/product/${id}`} className="inline-block text-primary-600 font-bold hover:underline text-sm">
                  Log In Now
                </Link>
              </div>
            )}
          </div>
          
          {/* Reviews list */}
          <div className="md:col-span-2 space-y-6">
            <h3 className="font-bold text-secondary-900 text-lg">Reviews ({product.reviews?.length || 0})</h3>
            {(!product.reviews || product.reviews.length === 0) ? (
              <div className="p-8 bg-secondary-50/50 rounded-3xl text-center border border-dashed border-secondary-200">
                <p className="text-secondary-500 font-medium">No reviews yet for this product. Be the first to share your experience!</p>
              </div>
            ) : (
              <div className="space-y-6 max-h-[500px] overflow-y-auto pr-2">
                {product.reviews.map((rev) => (
                  <div key={rev._id} className="bg-white rounded-3xl p-6 border border-secondary-100 shadow-sm space-y-3">
                    <div className="flex justify-between items-center">
                      <div className="space-y-0.5">
                        <p className="font-bold text-secondary-900 text-sm">{rev.name}</p>
                        <p className="text-[10px] text-secondary-400 font-bold">{new Date(rev.createdAt).toLocaleDateString()}</p>
                      </div>
                      <div className="flex items-center space-x-0.5">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star key={star} className={`h-3.5 w-3.5 ${star <= rev.rating ? 'text-yellow-400 fill-current' : 'text-secondary-200'}`} />
                        ))}
                      </div>
                    </div>
                    <p className="text-secondary-600 text-sm font-medium leading-relaxed">{rev.comment}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductScreen;
